import CredentialsProvider from 'next-auth/providers/credentials';

// import from the central services index would pull in `session.ts`
// (which brings in next-auth/next/react and their ESM deps).  using the
// underlying factory keeps this file lightweight and avoids circular
// transform issues in unit tests.

import { logger } from './logger';

// NextAuth configuration used throughout the application.  We rely on
// the credentials provider and a custom authorize() implementation to
// validate the customer against our backend API.  The backend login endpoint
// returns { access_token, refresh_token, token_type, user } on success.
// NextAuth stores these in a signed JWT cookie (strategy = 'jwt') so that
// client/server code can read tokens and user info from the session without
// additional API calls.
export const authOptions = {
  providers: [
    CredentialsProvider({
      name: 'credentials',
      credentials: {
        email: { label: 'Email', type: 'email' },
        password: { label: 'Password', type: 'password' },
        customer_type: { label: 'Customer Type', type: 'text', placeholder: 'optional for regular' },
      },
      async authorize(credentials) {
        if (!credentials?.email || !credentials?.password) {
          return null;
        }

        try {
          const authService = new (await import('@/services/modules/AuthService')).AuthService();
          const resp = await authService.loginCustomer(credentials.email, credentials.password, credentials.customer_type as string | undefined);

          logger.debug({ msg: 'Auth Response:', resp });

          const user = (resp as any)?.user;
          if (!user) {
            // backend did not return a user object – treat as authentication failure
            return null;
          }

          if (user.status === 'INACTIVE') {
            if (user.customer_type === 'REGULAR') {
              throw new Error('INACTIVE_REGULAR');
            } else {
              throw new Error('INACTIVE_BUSINESS');
            }
          }

          return {
            id: String(user.id),
            email: user.email,
            name: user.name,
            accessToken: (resp as any).access_token ?? null,
            refreshToken: (resp as any).refresh_token ?? null,
            customerType: user.customer_type,
          };
        } catch (error: any) {
          logger.error({ msg: 'Auth error:', error });
          // propagate message to signIn result
          throw error;
        }
      },
    }),
  ],
  session: {
    strategy: 'jwt',
  },
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async jwt({ token, user }: any) {
      if (user) {
        logger.debug({ msg: 'JWT callback user:', user });
        token.accessToken = user.accessToken;
        token.refreshToken = user.refreshToken;
        if (user.id) token.userId = user.id;
        if (user.email) token.userEmail = user.email;
        if (user.name) token.userName = user.name;
        if (user.customerType) token.customerType = user.customerType;
      }
      return token;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async session({ session, token }: any) {
      logger.debug({ msg: 'Session callback session:', session, token });
      session.accessToken = token.accessToken;
      session.refreshToken = token.refreshToken;
      if (token.customerType) session.customerType = token.customerType;
      if (token.userId) session.user.id = token.userId;
      if (token.userEmail) session.user.email = token.userEmail;
      if (token.userName) session.user.name = token.userName;
      return session;
    },
  },
  pages: {
    signIn: '/auth/signin',
    signOut: '/auth/signout',
  },
};

export default authOptions;
